import type { TransactionHistoryDetailViewModel } from 'src/api/types/TransactionHistoryDetailViewModel';

import { Box } from '@mui/material';

import { MESSAGES } from 'src/constants/messages';
import { formatDate, formatDollarAmount } from '../../../shared/utils/policyUtils';
import { TransactionHistoryDetailField } from './transaction-history-detail-field';

const UNKNOWN = MESSAGES.TRANSACTION_HISTORY.DETAIL_VALUE_UNKNOWN;

type DetailType = 'Cancellation' | 'Reinstatement' | 'Compliance' | 'Noncompliance';

type Props = {
  detailType: DetailType;
  details: TransactionHistoryDetailViewModel[];
  rowIndex: number;
};

/**
 * Unified component for rendering transaction history detail cards.
 * Supports Cancellation, Reinstatement, and Compliance/Noncompliance detail types.
 * Fields rendered vary by detail type but use consistent label/value card semantics.
 */
export function TransactionHistoryDetailCard({ detailType, details, rowIndex }: Readonly<Props>) {
  // Normalize Noncompliance to Compliance for display and testid purposes
  const normalizedType = detailType === 'Noncompliance' ? 'Compliance' : detailType;
  const testIdPrefix = `transaction-history-${normalizedType.toLowerCase()}`;

  return (
    <Box sx={{ px: 0, pb: 0 }} data-testid={`${testIdPrefix}-section-${rowIndex}`}>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: '1px solid var(--palette-divider)',
          p: 2,
          display: 'grid',
          gap: 1.5,
        }}
      >
        {details.map((detail, detailIndex) => (
          <Box
            key={`${detail.type ?? ''}-${detail.effectiveDate ?? ''}-${detailIndex}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 1,
              py: 1,
              borderTop: detailIndex === 0 ? 'none' : '1px dashed var(--palette-divider)',
            }}
            data-testid={`${testIdPrefix}-card-${rowIndex}-${detailIndex}`}
          >
            {renderDetailFields(normalizedType, detail, rowIndex, detailIndex)}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Renders type-specific detail fields based on normalizedType.
 * Each detail type has distinct fields: Cancellation/Reinstatement share 4 fields,
 * Compliance adds an additional outstandingPremiumDue field.
 */
function renderDetailFields(
  normalizedType: string,
  detail: TransactionHistoryDetailViewModel,
  rowIndex: number,
  detailIndex: number,
): React.ReactNode {
  const commonCancellationReinstateFields = (statusValue: string) => (
    <>
      <TransactionHistoryDetailField
        testId={`transaction-history-${normalizedType.toLowerCase()}-status-${rowIndex}-${detailIndex}`}
        label="Status"
        value={statusValue}
      />
      <TransactionHistoryDetailField
        testId={`transaction-history-${normalizedType.toLowerCase()}-type-${rowIndex}-${detailIndex}`}
        label="Type"
        value={detail.type ?? UNKNOWN}
      />
      <TransactionHistoryDetailField
        testId={`transaction-history-${normalizedType.toLowerCase()}-reason-${rowIndex}-${detailIndex}`}
        label="Reason"
        value={detail.reason ?? UNKNOWN}
      />
      <TransactionHistoryDetailField
        testId={`transaction-history-${normalizedType.toLowerCase()}-effective-date-${rowIndex}-${detailIndex}`}
        label="Effective date"
        value={detail.effectiveDate ? formatDate(new Date(detail.effectiveDate)) : UNKNOWN}
      />
    </>
  );

  switch (normalizedType) {
    case 'Cancellation':
      return commonCancellationReinstateFields('Cancellation');

    case 'Reinstatement':
      return commonCancellationReinstateFields('Reinstatement');

    case 'Compliance':
      return (
        <>
          <TransactionHistoryDetailField
            testId={`transaction-history-compliance-status-${rowIndex}-${detailIndex}`}
            label="Status"
            value={detail.detailType ?? detail.type ?? UNKNOWN}
          />
          <TransactionHistoryDetailField
            testId={`transaction-history-noncompliance-type-${rowIndex}-${detailIndex}`}
            label="Type"
            value={detail.type ?? UNKNOWN}
          />
          <TransactionHistoryDetailField
            testId={`transaction-history-compliance-reason-${rowIndex}-${detailIndex}`}
            label="Reason"
            value={detail.reason ?? UNKNOWN}
          />
          <TransactionHistoryDetailField
            testId={`transaction-history-compliance-effective-date-${rowIndex}-${detailIndex}`}
            label="Effective date"
            value={detail.effectiveDate ? formatDate(new Date(detail.effectiveDate)) : UNKNOWN}
          />
          <TransactionHistoryDetailField
            testId={`transaction-history-noncompliance-outstanding-premium-${rowIndex}-${detailIndex}`}
            label="Outstanding premium"
            value={
              detail.outstandingPremiumDue == null
                ? UNKNOWN
                : formatDollarAmount(detail.outstandingPremiumDue)
            }
          />
        </>
      );

    default:
      return null;
  }
}
